var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
var __read = this && this.__read || function (o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
};
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, entitiesWithValidCoordinates, entityTypeNameFor } from '@shapeable/utils';
import { MapMarker } from '../elements/map-marker';
import { useMapState, useMapUtils } from '../hooks/use-map-state';
import { EntityMapTooltip } from './entity-map-tooltip';
import { Map } from '../elements/map';
import { groupBy, map, mapValues, pickBy, toNumber, toString } from 'lodash';
import { EntityTooltip, useEntityTypeTooltips } from '@shapeable/ui';
import { EntityGroupMapMarker } from './entity-group-map-marker';
import { useEntityTypeMapMarkers } from '../hooks/use-entity-type-map-markers';
import { EntityMapMarker } from './entity-map-marker';
import { useMapConfig } from '../context/map-config-context';
var cls = classNames('entity-map');
export var EntityMapDefaultProps = {
  items: [],
  style: {},
  mapboxStyle: 'mapbox://styles/mapbox/streets-v11',
  tooltipComponent: EntityMapTooltip
};
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  \n  "], ["\n  \n  "])))
});
var MarkerStyles = breakpoints({
  base: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    font-size: ", ";\n    font-family: ", ";\n    width: 250px;\n  "], ["\n    font-size: ", ";\n    font-family: ", ";\n    width: 250px;\n  "])), theme.FONT_SIZE(13), theme.FONT('sans'))
});
var GroupMarkerStyles = breakpoints({
  base: css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  "], ["\n  "])))
});
var My = {
  Container: styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Map: styled(Map)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Marker: styled(MapMarker)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["", ""], ["", ""])), MarkerStyles),
  GroupMarker: styled(EntityGroupMapMarker)(templateObject_7 || (templateObject_7 = __makeTemplateObject(["", ""], ["", ""])), GroupMarkerStyles)
};
export var EntityMap = function (props) {
  var _a;
  var className = props.className,
    style = props.style,
    children = props.children,
    mapProps = __rest(props, ["className", "style", "children"]);
  var _b = useMapUtils(),
    deselect = _b.deselect,
    toggleSelectedEntity = _b.toggleSelectedEntity;
  var _c = __read(useMapState(), 2),
    mapState = _c[0],
    patchMapState = _c[1];
  var mapConfig = useMapConfig();
  var items = ((_a = mapConfig.items) === null || _a === void 0 ? void 0 : _a.call(mapConfig)) || props.items;
  var isInitialized = mapState.isInitialized,
    selectedEntity = mapState.selectedEntity,
    isVisible = mapState.isVisible,
    _d = mapState.entityTypeNames,
    entityTypeNames = _d === void 0 ? [] : _d;
  var onClose = deselect;
  var _e = __read(useState(new Date()), 2),
    lastDragDate = _e[0],
    setLastDragDate = _e[1];
  var _f = __read(useState(''), 2),
    selectedGroup = _f[0],
    setSelectedGroup = _f[1];
  var groupedEntities = React.useMemo(function () {
    // first, filter by entities with valid coordinates, i.e., are mappable
    var entities = entitiesWithValidCoordinates(items);
    // now assign these coordinate groups, so we can plot those as group markers OR standard markers
    var groupedEntities = mapValues(groupBy(entities, entityTypeNameFor), function (entities) {
      return groupBy(entities, function (entity) {
        return "".concat(toString(entity.latitude), ",").concat(toString(entity.longitude));
      });
    });
    return groupedEntities;
  }, [items, entityTypeNames]);
  var zoomLevelForSize = Math.max(1, mapState.zoom);
  var markerSize = Math.min(100, Math.floor(zoomLevelForSize) * 11) * 0.8;
  var markerPinSize = Math.min(40, Math.floor(mapState.zoom) * 9) * 0.7;
  var entityTypeIsVisible = function (entityTypeName) {
    return entityTypeNames.includes(entityTypeName);
  };
  var Tooltips = useEntityTypeTooltips();
  var Markers = useEntityTypeMapMarkers();
  var applyTypes = mapConfig.applyEntityTypeFilters ? mapConfig.applyEntityTypeFilters() : true;
  // now that we have our mappable entities, build markers
  var markers = React.useMemo(function () {
    var visibleGroupedEntities = !applyTypes ? groupedEntities : pickBy(groupedEntities, function (entities, entityTypeName) {
      return entityTypeIsVisible(entityTypeName);
    });
    return map(visibleGroupedEntities, function (entityCoordinateGroups, entityTypeName) {
      return map(entityCoordinateGroups, function (entities, coordinates) {
        var _a = __read(coordinates.split(',').map(toNumber), 2),
          lat = _a[0],
          lng = _a[1];
        var onMenuToggle = function (isOpen) {
          if (!isOpen) {
            deselect();
          }
          setSelectedGroup(isOpen ? coordinates : '');
        };
        var MarkerComponent = Markers[entityTypeName] || EntityMapMarker;
        var isSelectedGroup = coordinates === selectedGroup;
        var isSelected = entities.some(function (entity) {
          return (entity === null || entity === void 0 ? void 0 : entity.id) === (selectedEntity === null || selectedEntity === void 0 ? void 0 : selectedEntity.id);
        });
        var Tooltip = Tooltips[entityTypeName] || EntityTooltip;
        var popup = isSelected && React.createElement(Tooltip, {
          imageStyle: 'logo',
          entity: selectedEntity
        });
        return entities.length === 1 ? React.createElement(My.Marker, {
          isSelected: isSelected,
          key: coordinates,
          style: {
            opacity: isSelected ? 1.0 : !!selectedEntity ? 0.4 : 1.0,
            zIndex: isSelected || isSelectedGroup ? 199 : 98
          },
          items: entities,
          longitude: lng,
          latitude: lat,
          onClose: deselect,
          onClick: function () {
            toggleSelectedEntity(entities[0]);
          },
          popup: popup,
          zoom: mapState.zoom
        }, React.createElement(MarkerComponent, {
          isSelected: isSelected,
          size: markerSize,
          pinSize: markerPinSize,
          entity: entities[0]
        })) : React.createElement(My.GroupMarker, {
          key: coordinates,
          popup: popup,
          style: {
            opacity: isSelected ? 1.0 : !!selectedEntity ? 0.4 : 1.0,
            zIndex: isSelectedGroup || isSelectedGroup ? 200 : 99
          },
          longitude: lng,
          latitude: lat,
          selectedEntity: selectedEntity,
          onMenuToggle: onMenuToggle,
          onSelect: toggleSelectedEntity,
          size: markerSize,
          entityTypeName: entityTypeName,
          items: entities
        });
      });
    });
  }, [groupedEntities, selectedEntity, lastDragDate, isInitialized]);
  return React.createElement(My.Map, __assign({}, mapProps, {
    className: cls.name(className),
    style: style,
    onInteractionEnd: function () {
      return setLastDragDate(new Date());
    }
  }), markers, children);
};
EntityMap.defaultProps = EntityMapDefaultProps;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;